import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './UsageUsersList.module.scss';
import Pagination from '../../../../../components/Pagination/Pagination';
import usePagination from '../../../../../hooks/usePagination';
import VideoService from '../../../../../service/VideoService';
import { formatDate } from '../../utils';
import { useTranslation } from 'react-i18next';
import SearchComponent from '../../../../../components/SearchComponent/SearchComponent';
import Icon from '../../../../../components/Icon/Icon';
import UsageLoader from '../UsageLoader/UsageLoader';
import UsageUserItem from '../UsageUserItem/UsageUserItem';
import UsageHoverLabel from '../UsageHoverLabel/UsageHoverLabel';
import RecordNotFound from '../../../../../components/_empty_state/RecordNotFound';

const UsageUsersList = ({ companyId, contract }) => {
  const [init, setInit] = useState(false);
  const [prevPageHeader, setPrevPageHeader] = useState(null);

  const { t } = useTranslation();

  const getUsagePerUser = useCallback(
    async params => {
      try {
        const response = await VideoService.getUsagePerUser(companyId, contract.id, params);
        return response;
      } catch (err) {
        console.log(err);
      } finally {
        setInit(true);
      }
    },
    [companyId, contract]
  );

  const containerRef = useRef(null);

  const {
    list: usersData,
    query,
    totalData,
    isLoading: isUsersLoading,
    onSearch,
    onChangePage,
    onChangeFilter,
    onSort,
    loadList,
    onResetQuery,
  } = usePagination({
    service: getUsagePerUser,
  });

  const handleChangePage = useCallback((page) => {
    containerRef.current && setPrevPageHeader(containerRef?.current?.offsetHeight);
    onChangePage(page);
  }, [onChangePage])

  const handleSort = field => {
    containerRef.current && setPrevPageHeader(containerRef?.current?.offsetHeight);
    if (query.sortBy === field) {
      onSort({ sortBy: field, sortOrder: query.sortOrder === 'asc' ? 'desc' : 'asc' });
      return;
    }

    onSort({ sortBy: field, sortOrder: 'asc' });
  };

  const getSortIcon = useCallback(
    type => {
      if (type === query.sortBy) {
        if (query.sortOrder === 'asc') {
          return 'sortTop';
        }

        if (query.sortOrder === 'desc') {
          return 'sortBottom';
        }
      }

      return 'sort';
    },
    [query]
  );

  useEffect(() => {
    init && onResetQuery();
  }, [contract.id, companyId]);

  return (
    <div className={styles.sectionWrapper}>
      <div className={styles.titleWrapper}>
        <h3 className={styles.title}>
          {t('company.settings.usage.per_user.title')} ({formatDate(contract.startDate)} - {formatDate(contract.endDate)})
        </h3>
        <SearchComponent searchValue={query.search} onChange={onSearch} clearButton />
      </div>
      {isUsersLoading ? (
        <UsageLoader style={{ height: `${prevPageHeader}px` }} />
      ) : (
        <div ref={containerRef}>
          <div className={styles.header}>
            <div onClick={() => handleSort('name')}>
              <span className={styles.headerTitle}>{t('company.settings.users.name')}</span>
              <Icon src={getSortIcon('name')} verticalAlign="middle" className={styles.sortIcon} />
            </div>
            <div onClick={() => handleSort('email')}>
              <span className={styles.headerTitle}>{t('company.settings.users.email')}</span>
              <Icon src={getSortIcon('email')} verticalAlign="middle" className={styles.sortIcon} />
            </div>
            <div onClick={() => handleSort('totalVideos')}>
              <UsageHoverLabel
                className={styles.headerTitle}
                text={t('company.settings.usage.per_user.title.total_videos')}
                hoverText={t('company.settings.usage.per_user.title.total_videos.hover')}
              />
              <Icon src={getSortIcon('totalVideos')} verticalAlign="middle" className={styles.sortIcon} />
            </div>
            <div onClick={() => handleSort('processed')}>
              <UsageHoverLabel
                className={styles.headerTitle}
                text={t('company.settings.usage.used')}
                hoverText={t('company.settings.usage.per_user.title.used.hover')}
              />
              <Icon src={getSortIcon('processed')} verticalAlign="middle" className={styles.sortIcon} />
            </div>
            <div onClick={() => handleSort('deleted')}>
              <UsageHoverLabel
                className={styles.headerTitle}
                text={t('company.settings.usage.deleted')}
                hoverText={t('company.settings.usage.per_user.title.deleted.hover')}
              />
              <Icon src={getSortIcon('deleted')} verticalAlign="middle" className={styles.sortIcon} />
            </div>
            <div onClick={() => handleSort('total')}>
              <UsageHoverLabel
                className={styles.headerTitle}
                text={t('company.settings.usage.total')}
                hoverText={t('company.settings.usage.per_user.title.total.hover')}
              />
              <Icon src={getSortIcon('total')} verticalAlign="middle" className={styles.sortIcon} />
            </div>
          </div>
          <ul className={styles.listWrapper}>
            {usersData?.map(({ username, email, name, videos, minutes }) => (
              <UsageUserItem key={username || email || `user-${Math.random()}`} email={email} name={name} videos={videos} minutes={minutes} />
            ))}
          </ul>
          {usersData.length === 0 && query.search && !isUsersLoading && <RecordNotFound searchTerm={query.search} />}
        </div>
      )}
      {!!totalData && (
        <div className={styles.paginationWrapper}>
          <Pagination currentPage={query.page} totalPages={Math.ceil(totalData / query.perPage)} changePage={handleChangePage} />
        </div>
      )}
    </div>
  );
};

export default UsageUsersList;
